import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, BrowserRouter } from "react-router-dom";
import { useMsal } from '@azure/msal-react';
import { Spin, message } from 'antd';
import axios from 'axios';
import './App.css';

import { RouteTracker } from './utils/appInsights';
import Authentication from './pages/Authentication';
import AccessDeniedPage from './pages/AccessDeniedPage';
import ProtectedRoute from './components/ProtectedRoute';

const ChatPage = lazy(() => import('./pages/ChatPage'));
const ChatListPage = lazy(() => import('./pages/ChatListPage'));
const FeedbackPage = lazy(() => import('./pages/user-analytics/FeedbackPage'));
const QueryListPage = lazy(() => import('./pages/user-analytics/QueryListPage'));
const GeneralStatisticsPage = lazy(() => import('./pages/user-analytics/GeneralStatisticsPage'));
const PipelineStatisticsPage = lazy(() => import('./pages/ocr-admin/PipelineStatisticsPage'));
const ObservabilityPage = lazy(() => import('./pages/ocr-admin/ObservabilityPage'));
const TokenClassificationPage = lazy(() => import('./pages/costing-admin/TokenClassificationPage'));
const AzureRGPage = lazy(() => import('./pages/costing-admin/AzureRGPage'));
const GCPPage = lazy(() => import('./pages/costing-admin/GCPPage'));
const LlmPlaygroundPage = lazy(() => import('./pages/llm-swap/LlmPlaygroundPage'));
const DocConfigPage = lazy(() => import('./pages/ocr-admin/DocConfigPage'));

const LOADING_FALLBACK = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
);

const protectedRoutes: { path: string; element: React.ReactElement }[] = [
  { path: '/chat',                              element: <ChatPage /> },
  { path: '/chat/:id',                          element: <ChatPage /> },
  { path: '/chat-list',                         element: <ChatListPage /> },
  { path: '/user-analytics/feedback',           element: <FeedbackPage /> },
  { path: '/user-analytics/query-list',         element: <QueryListPage /> },
  { path: '/user-analytics/general-statistics', element: <GeneralStatisticsPage /> },
  { path: '/llm-swap/llm-playground',           element: <LlmPlaygroundPage /> },
  { path: '/ocr/pipeline-statistics',           element: <PipelineStatisticsPage /> },
  { path: '/ocr/observability',                 element: <ObservabilityPage /> },
  { path: '/ocr/doc-config',                    element: <DocConfigPage /> },
  { path: '/costing/token-classification',      element: <TokenClassificationPage /> },
  { path: '/costing/azure-rg',                  element: <AzureRGPage /> },
  { path: '/costing/gcp',                       element: <GCPPage /> },
];

function App() {
  const { instance } = useMsal();

  useEffect(() => {
    const sessionExpired = localStorage.getItem('session_expired') === 'true';
    const expiry = sessionStorage.getItem('tokenExpiry');
    const tokenValid = expiry ? new Date(expiry) > new Date() : false;
    if (sessionExpired || !tokenValid) return;
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]); // ensure AuthenticatedTemplate works after reload
    }
  }, [instance]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.status === 401 &&
          error.response?.data?.detail?.includes('Token has expired')
        ) {
          message.error('JWT validation failed: Token has expired', 5);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  return (
    <BrowserRouter>
      <RouteTracker />
      <div className="App">
        <Suspense fallback={LOADING_FALLBACK}>
          <Routes>
            <Route index element={<Authentication />} />
            <Route path="/login" element={<Authentication />} />
            <Route path="/access-denied" element={<AccessDeniedPage />} />
            {protectedRoutes.map(({ path, element }) => (
              <Route
                key={path}
                path={path}
                element={<ProtectedRoute>{element}</ProtectedRoute>}
              />
            ))}
          </Routes>
        </Suspense>
      </div>
    </BrowserRouter>
  );
}

export default App;
